import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { toast } from "sonner";
import { Crown, User as UserIcon, Megaphone, ChevronDown } from "lucide-react";
import SocialLoginExtras from "../components/thqah/SocialLoginExtras";
import { GULF_COUNTRIES, DEFAULT_DIAL, isValidLocalNumber, formatPhone } from "../lib/gulfPhone";
import { applyAsInfluencer } from "../lib/whatsapp";

const PROFILE_TYPES = [
  { id: "owner",      labelAr: "مالك حلال",            labelEn: "Livestock Owner",       icon: UserIcon  },
  { id: "influencer", labelAr: "مؤثر متخصص",            labelEn: "Specialty Influencer",  icon: Megaphone },
];

const SPECIALTIES = [
  { id: "camel",  labelAr: "إبل",   labelEn: "Camels"  },
  { id: "horse",  labelAr: "خيل",   labelEn: "Horses"  },
  { id: "falcon", labelAr: "صقور",  labelEn: "Falcons" },
];

export default function Register() {
  const { register } = useAuth();
  const { t, lang } = useI18n();
  const ar = lang === "ar";
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    referral_code: "",
  });
  const [dial, setDial] = useState(DEFAULT_DIAL);
  const [local, setLocal] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [profileType, setProfileType] = useState("owner");
  const [specialty, setSpecialty] = useState("camel");
  const [loading, setLoading] = useState(false);

  const u = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Phone is OPTIONAL per Apple Guideline 5.1.1(v) — Data Collection.
    // Only validate when the user actually entered one.
    if (local && !isValidLocalNumber(local)) {
      toast.error(ar ? "أدخل رقم جوال صحيح" : "Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        role: "user",
        intent: profileType,
        specialty,
      };
      if (local && isValidLocalNumber(local)) {
        payload.phone = formatPhone(dial, local);
      }
      if (profileType !== "owner") {
        localStorage.setItem("thqah_signup_intent", JSON.stringify({ type: profileType, specialty }));
      } else {
        localStorage.removeItem("thqah_signup_intent");
      }
      await register(payload);
      toast.success(ar ? "تم إنشاء الحساب" : "Account created");
      if (profileType === "influencer") {
        nav("/dashboard");
        // Open WhatsApp so the new user can immediately apply via Thqah support.
        setTimeout(() => applyAsInfluencer(), 300);
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (ar ? "فشل التسجيل" : "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  const activeCountry = GULF_COUNTRIES.find((c) => c.code === dial) || GULF_COUNTRIES[0];

  return (
    <div className="mx-auto max-w-md px-5 py-12" data-testid="register-page">
      <div className="flex flex-col items-center gap-3 mb-7">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "linear-gradient(135deg, #F9E596, #AA8000)" }}>
          <Crown className="h-6 w-6 text-black" />
        </span>
        <h1 className="text-3xl font-black gold-text">{t("app_name")}</h1>
        <p className="text-white/60 text-sm">{t("sign_up_cta")}</p>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
        {/* Profile-type chooser */}
        <div>
          <label className="text-xs text-white/70 uppercase tracking-wider mb-2 block">
            {ar ? "نوع الحساب" : "Account type"}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {PROFILE_TYPES.map((p) => {
              const Icon = p.icon;
              const active = profileType === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setProfileType(p.id)}
                  className={`rounded-xl border p-3 flex flex-col items-center gap-1.5 transition-all ${
                    active
                      ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]"
                      : "bg-black/30 border-white/10 text-white/70 hover:border-white/30"
                  }`}
                  data-testid={`reg-profile-${p.id}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-[11px] font-bold leading-tight text-center">
                    {ar ? p.labelAr : p.labelEn}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Specialty chooser */}
        <div>
          <label className="text-xs text-white/70 uppercase tracking-wider mb-2 block">
            {ar ? "التخصص" : "Specialty"}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {SPECIALTIES.map((s) => {
              const active = specialty === s.id;
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setSpecialty(s.id)}
                  className={`rounded-xl border py-2.5 text-sm font-semibold transition-all ${
                    active
                      ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]"
                      : "bg-black/30 border-white/10 text-white/75 hover:border-white/30"
                  }`}
                  data-testid={`reg-specialty-${s.id}`}
                >
                  {ar ? s.labelAr : s.labelEn}
                </button>
              );
            })}
          </div>
          {profileType !== "owner" && (
            <p className="text-[10px] text-white/55 mt-2">
              {ar
                ? "بعد إنشاء الحساب، ستُحوَّل لصفحة تقديم الطلب لإكمال البيانات."
                : "After signup, you'll be taken to a short application form."}
            </p>
          )}
        </div>

        <Field label={t("full_name")} value={form.full_name} onChange={u("full_name")} testid="reg-full-name" required />
        <Field label={t("email")} type="email" value={form.email} onChange={u("email")} testid="reg-email" required />

        {/* Phone with Gulf-only dial code picker (OPTIONAL per Apple 5.1.1(v)) */}
        <div>
          <label className="text-xs text-white/70 uppercase tracking-wider">
            {t("phone")}
            <span className="text-white/40 normal-case tracking-normal ms-1">
              ({ar ? "اختياري" : "optional"})
            </span>
          </label>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen((s) => !s)}
              className="h-11 px-3 bg-black/40 border border-white/10 hover:border-[#D4AF37]/50 rounded-md inline-flex items-center gap-1.5 text-sm font-bold text-white"
              data-testid="reg-dial-toggle"
            >
              <span className="text-base">{flag(activeCountry.iso)}</span>
              <span>{activeCountry.code}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
            <input
              type="tel"
              inputMode="numeric"
              value={local}
              onChange={(e) => setLocal(e.target.value.replace(/\D+/g, "").slice(0, 10))}
              placeholder="500000000"
              dir="ltr"
              className="flex-1 h-11 bg-black/40 border border-white/10 focus:border-[#D4AF37] outline-none rounded-md px-3 text-sm tracking-wider"
              data-testid="reg-phone"
            />
          </div>
          {pickerOpen && (
            <ul
              className="mt-2 max-h-44 overflow-y-auto rounded-md bg-black/85 border border-white/10"
              role="listbox"
              data-testid="reg-dial-list"
            >
              {GULF_COUNTRIES.map((c) => (
                <li key={c.iso}>
                  <button
                    type="button"
                    onClick={() => { setDial(c.code); setPickerOpen(false); }}
                    className={`w-full text-start px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#D4AF37]/10 ${dial === c.code ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-white/85"}`}
                    data-testid={`reg-dial-${c.iso}`}
                  >
                    <span>{flag(c.iso)}</span>
                    <span className="flex-1">{ar ? c.labelAr : c.labelEn}</span>
                    <span className="font-mono text-xs text-white/55">{c.code}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Field label={t("password")} type="password" value={form.password} onChange={u("password")} testid="reg-password" required />
        <Field label={t("referral_code")} value={form.referral_code} onChange={u("referral_code")} testid="reg-referral" />

        <button
          disabled={loading}
          type="submit"
          className="btn-gold w-full rounded-md py-3 font-bold"
          data-testid="reg-submit"
        >
          {loading
            ? t("loading")
            : profileType === "owner"
              ? t("register")
              : ar
                ? "إنشاء الحساب ومتابعة"
                : "Create account & continue"}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-white/40 uppercase tracking-wider">{t("or")}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          type="button"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            if (form.referral_code) localStorage.setItem("thqah_pending_ref", form.referral_code.toUpperCase());
            if (profileType !== "owner") {
              localStorage.setItem("thqah_signup_intent", JSON.stringify({ type: profileType, specialty }));
            }
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full rounded-md py-3 font-semibold text-sm bg-white text-black hover:bg-white/90 inline-flex items-center justify-center gap-2"
          data-testid="google-signin"
        >
          <svg className="h-5 w-5" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.4-4.6 2.3-7.3 2.3-5.1 0-9.5-3.3-11.2-7.9l-6.6 5.1C9.5 39.5 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3c-.4.4 6.4-4.7 6.4-14.3 0-1.3-.1-2.4-.4-3.5z" />
          </svg>
          {t("sign_in_google")}
        </button>
        <SocialLoginExtras />
        <p className="text-center text-sm text-white/60">
          {t("already_have")}{" "}
          <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="to-login">
            {t("login")}
          </Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs text-white/70 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-1 w-full bg-black/40 border border-white/10 focus:border-[#D4AF37] focus:outline-none rounded-md px-3 py-2.5 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function flag(iso) {
  if (!iso || iso.length !== 2) return "🌍";
  const A = 0x1f1e6;
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => A + c.charCodeAt(0) - 65));
}
